import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Printer, RefreshCw, Loader2, CheckCircle2, AlertTriangle, Send, Star } from "lucide-react";
import { listDevices, printZpl, printIdentifyLabel } from "@/lib/zebra";
import {
  MEDIA_TYPES, MEDIA_LABEL, getPrinterMap, setPrinterStock, setDefaultPrinterFor,
  resolveDeviceForMedia, printersForMedia, printerDisplayName, stockOf,
  getPrinterNicknames, setPrinterNickname, hydratePrinterConfig, savePrinterConfig,
} from "@/lib/printerMap";

// Identify test — plain text on whatever stock is loaded, by design (this is the one job
// with no media: it answers "which physical unit is this row?"). Never prints the serial.
const identifyZpl = (d) =>
  `^XA^CI28^CF0,40^FO30,30^FDPRINTER ID^FS^CF0,32^FO30,90^FD${printerDisplayName(d).slice(0, 26)}^FS` +
  `^CF0,24^FO30,145^FDunit ...${String(d.uid).slice(-4)} - identify test^FS^FO30,185^GB560,3,3^FS^XZ`;

const mediaTestZpl = (label, d) =>
  `^XA^CI28^CF0,44^FO30,28^FD${label.slice(0, 22)}^FS^CF0,28^FO30,92^FDroutes to:^FS` +
  `^CF0,34^FO30,132^FD${printerDisplayName(d).slice(0, 26)}^FS^FO30,188^GB560,3,3^FS` +
  `^CF0,24^FO30,206^FDmedia routing test - not a real label^FS^XZ`;

export default function PrinterSettingsPage() {
  const [devices, setDevices] = useState(null);
  const [map, setMap] = useState(getPrinterMap());
  const [nicks, setNicks] = useState(getPrinterNicknames());
  const [testing, setTesting] = useState("");
  const [, setV] = useState(0);   // force re-render after a local config change

  const refresh = useCallback(async () => {
    setDevices(null);
    await hydratePrinterConfig(true);
    setMap(getPrinterMap()); setNicks(getPrinterNicknames());
    try { setDevices(await listDevices(true)); }
    catch { setDevices([]); toast.error("Browser Print not reachable — check it's running."); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  // Every change is written locally then PERSISTED — and a save that can't reach the
  // server is SHOWN and rolled back to server truth, never left looking saved.
  const persist = async (okMsg) => {
    setMap(getPrinterMap()); setNicks(getPrinterNicknames()); setV((v) => v + 1);
    try {
      await savePrinterConfig();
      toast.success(okMsg);
    } catch (e) {
      const denied = e?.response?.status === 403;
      toast.error(denied
        ? "Not saved — you need the Manage Settings permission to change printer names or stock. Nothing was changed for anyone."
        : "Not saved — the server didn't accept the change, so nothing changed for anyone. Check your connection and try again.");
      await hydratePrinterConfig(true);            // roll back to what everyone else sees
      setMap(getPrinterMap()); setNicks(getPrinterNicknames()); setV((v) => v + 1);
    }
  };

  // A printer holds ONE stock. Several printers MAY hold the same stock (a spare) — the
  // first becomes that stock's default; the others are legal overrides at print time.
  const assignStock = (uid, mediaKey) => {
    setPrinterStock(uid, mediaKey);
    persist(mediaKey ? `Saved — this printer holds ${MEDIA_LABEL[mediaKey]}.` : "Saved — stock cleared for this printer.");
  };
  const makeDefault = (uid, mediaKey) => {
    setDefaultPrinterFor(mediaKey, uid);
    persist(`Saved — ${printerDisplayName({ uid })} is now the default ${MEDIA_LABEL[mediaKey]} printer.`);
  };
  const renamePrinter = (uid, name) => {
    if ((getPrinterNicknames()[uid] || "") === (name || "").trim()) return;   // no-op blur
    setPrinterNickname(uid, name);
    persist(name && name.trim() ? `Saved — this printer is now "${name.trim()}" everywhere.` : "Saved — name cleared.");
  };

  // QR-only 3x1 fallback (server-backed, OFF by default): drops the dense 27-char Code 128
  // from part + truck 3x1 labels if the physical scan-density test fails at the gun.
  const [qrOnly, setQrOnly] = useState(null);
  useEffect(() => { api.get("/tags/printer-config").then(({ data }) => setQrOnly(!!data.qr_only_3x1)).catch(() => setQrOnly(false)); }, []);
  const saveQrOnly = async (v) => {
    setQrOnly(v);
    try {
      await savePrinterConfig({ qr_only_3x1: v });
      toast.success(v ? "3×1 part & truck labels now print QR-ONLY (no Code 128)." : "3×1 part & truck labels back to QR + Code 128.");
    } catch (e) {
      setQrOnly(!v);
      toast.error(e?.response?.status === 403 ? "You need the Manage Settings permission to change this." : "Could not save — try again.");
    }
  };

  const testMediaRoute = async (m) => {
    const target = resolveDeviceForMedia(m.key, devices || []);
    if (!target) { toast.error(`No printer holds ${m.label} — set the stock above first. Nothing was printed.`); return; }
    setTesting(`media-${m.key}`);
    try {
      await printZpl(mediaTestZpl(m.label, target), null, m.key);   // routed by the real guard
      toast.success(`Sent "${m.label}" test → ${printerDisplayName(target)}. Confirm it came out on the right unit.`);
    } catch (e) { toast.error(e.message || "Test failed."); }
    finally { setTesting(""); }
  };

  const identify = async (d) => {
    setTesting(d.uid);
    try {
      await printIdentifyLabel(d, identifyZpl(d));
      toast.success(`Identify label sent to ${printerDisplayName(d)} — watch which unit prints it. It prints on whatever stock is loaded.`);
    } catch (e) { toast.error(e.message || "Test failed."); }
    finally { setTesting(""); }
  };

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-6 space-y-8" data-testid="printer-settings-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-black tracking-tight flex items-center gap-2"><Printer className="w-7 h-7 text-[#B01E28]" />Printer Setup</h1>
          <p className="text-slate-500 mt-1">Name each printer and tell it what stock it holds. Every label then routes itself — you only choose the label, never the printer. Saved on the server, so it follows the account to every PC and tablet and survives an update.</p>
        </div>
        <Button variant="outline" onClick={refresh} data-testid="printers-refresh"><RefreshCw className="w-4 h-4 mr-1.5" />Refresh</Button>
      </div>

      <section className="space-y-2">
        <div className="text-xs font-bold uppercase tracking-widest text-slate-400">Your printers — name them, and set what stock each holds</div>
        {devices === null && <div className="flex justify-center py-8"><Loader2 className="w-7 h-7 animate-spin text-slate-400" /></div>}
        {devices && devices.length === 0 && (
          <div className="rounded-lg border-2 border-dashed border-amber-200 bg-amber-50 p-4 text-amber-800 flex items-center gap-2" data-testid="printers-none">
            <AlertTriangle className="w-5 h-5" />No printers found. Make sure Zebra Browser Print is running and the printers are connected, then Refresh.
          </div>
        )}
        <div className="space-y-2">
          {(devices || []).map((d) => {
            const { stock, source } = stockOf(d);
            const isDefault = stock && map[stock] === d.uid;
            return (
              <div key={d.uid} data-testid={`printer-${d.uid}`} className="rounded-lg border border-slate-200 bg-white p-3 flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <input data-testid={`printer-nick-${d.uid}`} defaultValue={nicks[d.uid] || ""}
                    onBlur={(e) => renamePrinter(d.uid, e.target.value)}
                    placeholder={printerDisplayName(d)}
                    className="font-bold text-slate-900 bg-transparent border-b border-dashed border-slate-200 focus:border-[#B01E28] outline-none w-full max-w-[280px] truncate" />
                  <div className="text-[11px] mt-0.5" data-testid={`printer-stock-state-${d.uid}`}>
                    {source === "assigned" && (
                      <span className="text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />Holds <b>{MEDIA_LABEL[stock]}</b>
                        {isDefault
                          ? <span className="inline-flex items-center gap-0.5 text-emerald-800"><Star className="w-3 h-3" />default</span>
                          : <span className="text-sky-700">· spare</span>}
                      </span>
                    )}
                    {source === "guess" && (
                      <span className="text-sky-700 inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" />Guessed <b>{MEDIA_LABEL[stock]}</b> from its name — a guess, not your setting. Pick it below to make it real.</span>
                    )}
                    {source === "none" && (
                      <span className="text-[#B01E28] font-semibold inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" />No stock set — this printer will refuse every label until you set one</span>
                    )}
                    <span className="text-slate-300 ml-2 font-mono">…{String(d.uid).slice(-4)}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <select data-testid={`printer-stock-${d.uid}`} value={source === "assigned" ? stock : ""} onChange={(e) => assignStock(d.uid, e.target.value)}
                    className="h-9 rounded-md border border-slate-300 bg-white px-2 text-sm min-w-[190px]">
                    <option value="">{source === "guess" ? `Guess: ${MEDIA_LABEL[stock]} (not set)` : "— not assigned —"}</option>
                    {MEDIA_TYPES.map((m) => <option key={m.key} value={m.key}>Holds {m.label}</option>)}
                  </select>
                  {source === "assigned" && !isDefault && (
                    <Button size="sm" variant="outline" data-testid={`printer-make-default-${d.uid}`} onClick={() => makeDefault(d.uid, stock)}>
                      <Star className="w-4 h-4 mr-1.5" />Make default
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => identify(d)} disabled={!!testing} data-testid={`printer-test-${d.uid}`}>
                    {testing === d.uid ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1.5" />Identify</>}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
        <p className="text-xs text-slate-400">Two printers may hold the same stock — the first is the <b>default</b>, the other is a <b>spare</b> the crew can pick at print time. A printer can never receive a layout that doesn't match its stock. <b>Identify</b> prints a name label on whatever stock is loaded so you can see which unit a row is.</p>
      </section>

      {/* Read-only routing check: confirms every stock resolves to a printer. */}
      <section className="space-y-2">
        <div className="text-xs font-bold uppercase tracking-widest text-slate-400">Routing check — where each label prints</div>
        <div className="rounded-lg border border-slate-200 divide-y divide-slate-100">
          {MEDIA_TYPES.map((m) => {
            const resolved = resolveDeviceForMedia(m.key, devices || []);
            const holders = printersForMedia(m.key, devices || []);
            const byGuess = resolved && resolved.matchedBy === "name";
            return (
              <div key={m.key} data-testid={`media-row-${m.key}`} className="flex flex-wrap items-center gap-3 p-3">
                <div className="flex-1 min-w-[160px]">
                  <div className="font-semibold text-slate-900">{m.label}</div>
                  {resolved ? (
                    <div className="text-[11px] mt-0.5 flex items-center gap-1" data-testid={`media-resolved-${m.key}`}>
                      <span className={`${byGuess ? "text-sky-700" : "text-emerald-700"} inline-flex items-center gap-1`}><CheckCircle2 className="w-3 h-3" />Prints to <b>{printerDisplayName(resolved)}</b>{byGuess ? " (name guess — set it above to lock it)" : ""}
                        {holders.length > 1 ? <span className="text-slate-500">· {holders.length - 1} spare{holders.length - 1 === 1 ? "" : "s"}</span> : null}</span>
                    </div>
                  ) : (
                    <div className="text-[11px] mt-0.5 text-[#B01E28] font-semibold flex items-center gap-1" data-testid={`media-resolved-${m.key}`}><AlertTriangle className="w-3 h-3" />Not set — this label will REFUSE to print until a printer holds it above</div>
                  )}
                </div>
                <Button size="sm" variant="outline" disabled={!!testing || !(devices || []).length || !resolved}
                  onClick={() => testMediaRoute(m)} data-testid={`media-test-${m.key}`}>
                  {testing === `media-${m.key}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1.5" />Test route</>}
                </Button>
              </div>
            );
          })}
        </div>
        <p className="text-xs text-slate-400">This mirrors the printer assignments above and uses the same routing the real print jobs use. Hit <b>Test route</b> to confirm that stock lands on the right unit.</p>
      </section>

      {/* QR-only 3x1 fallback — ready to switch on if the scan-density test fails at the gun. */}
      <section className="space-y-2" data-testid="qr-only-section">
        <div className="text-xs font-bold uppercase tracking-widest text-slate-400">3×1 barcode mode</div>
        <div className="rounded-lg border border-slate-200 bg-white p-4 flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-slate-900">QR-only 3×1 labels (parts &amp; truck tags)</div>
            <p className="text-xs text-slate-500 mt-1">
              The 3×1 squeezes a dense 27-character Code 128 into ~1.9″ — if it won't gun-scan reliably,
              flip this ON: part and truck 3×1 labels drop the barcode and give the QR + readable text the
              space. Bin/location 3×1s are unaffected (their barcode is short and safe). Applies to every
              new print immediately; already-printed labels don't change.
            </p>
          </div>
          {qrOnly === null
            ? <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
            : <Switch data-testid="qr-only-toggle" checked={qrOnly} onCheckedChange={saveQrOnly} />}
        </div>
      </section>
    </div>
  );
}
